/**
 * PDF Generation Service
 *
 * Provides utilities for generating PDFs from HTML/Markdown content.
 * Uses html2pdf.js for client-side PDF generation.
 */

import i18n from '../i18n/config';

export interface PDFGenerationOptions {
  /** Content to convert (HTML string) */
  htmlContent: string;
  /** Filename for the PDF (without extension) */
  filename: string;
  /** Optional title to display in the PDF */
  title?: string;
  /** Page margins in mm [top, right, bottom, left] */
  margins?: [number, number, number, number];
  /** Image quality (0-1) */
  imageQuality?: number;
  /** Scale factor for rendering */
  scale?: number;
}

export interface MarkdownToPDFOptions {
  /** Markdown content to convert */
  markdownContent: string;
  /** Filename for the PDF (without extension) */
  filename: string;
  /** Entity name for display */
  entityName?: string;
  /** Entity type for display */
  entityType?: 'use_case' | 'cadrage' | 'domain' | 'other';
}

export interface MarkdownToDocxOptions {
  /** Markdown content to convert */
  markdownContent: string;
  /** Filename for the DOCX (without extension) */
  filename: string;
  /** Optional document title */
  title?: string;
}

type DocxParagraph = {
  text?: string;
  heading?: string;
  bullet?: { level: number };
  children?: unknown[];
  spacing?: { after?: number };
};

type DocxRuntime = {
  Paragraph: new (options?: DocxParagraph) => unknown;
  TextRun: new (options: string | Record<string, unknown>) => unknown;
  HeadingLevel: Record<string, string>;
  Document: new (options: Record<string, unknown>) => unknown;
  Packer: {
    toBlob(document: unknown): Promise<Blob>;
  };
};

/**
 * Get print-friendly CSS styles for PDF generation
 */
const getPrintStyles = (): string => `
  * { color: #1a1a1a !important; }
  h1 { font-size: 24px !important; margin-bottom: 16px !important; border-bottom: 2px solid #333 !important; padding-bottom: 8px !important; color: #111 !important; }
  h2 { font-size: 20px !important; margin-top: 24px !important; margin-bottom: 12px !important; color: #222 !important; }
  h3 { font-size: 16px !important; margin-top: 20px !important; margin-bottom: 8px !important; color: #333 !important; }
  p { margin-bottom: 12px !important; color: #1a1a1a !important; }
  ul, ol { margin-bottom: 12px !important; padding-left: 24px !important; }
  li { margin-bottom: 4px !important; color: #1a1a1a !important; }
  strong { font-weight: 600 !important; color: #1a1a1a !important; }
  code { background: #f5f5f5 !important; color: #333 !important; padding: 2px 6px !important; border-radius: 4px !important; }
  pre { background: #f5f5f5 !important; color: #333 !important; padding: 16px !important; border-radius: 8px !important; overflow-x: auto !important; }
  table { width: 100% !important; border-collapse: collapse !important; margin: 16px 0 !important; }
  th { background: #f5f5f5 !important; color: #333 !important; border: 1px solid #ddd !important; padding: 8px 12px !important; text-align: left !important; font-weight: 600 !important; }
  td { color: #333 !important; border: 1px solid #ddd !important; padding: 8px 12px !important; text-align: left !important; }
  blockquote { border-left: 4px solid #ddd !important; margin: 16px 0 !important; padding-left: 16px !important; color: #666 !important; }
  hr { border: none !important; border-top: 1px solid #ddd !important; margin: 24px 0 !important; }
  a { color: #0066cc !important; }
`;

/**
 * Generate a PDF from HTML content
 */
export const generatePDFFromHTML = async (options: PDFGenerationOptions): Promise<void> => {
  const {
    htmlContent,
    filename,
    margins = [15, 15, 15, 15],
    imageQuality = 0.98,
    scale = 2,
  } = options;

  try {
    // Dynamically import html2pdf to avoid SSR issues
    const html2pdf = (await import('html2pdf.js')).default;

    // Create a container with print-friendly styles
    const container = document.createElement('div');
    container.innerHTML = `
      <div style="
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        line-height: 1.7;
        color: #1a1a1a;
        background: #ffffff;
        padding: 20px;
        max-width: 800px;
      ">
        <style>${getPrintStyles()}</style>
        ${htmlContent}
      </div>
    `;

    const opt = {
      margin: margins,
      filename: `${filename}.pdf`,
      image: { type: 'jpeg' as const, quality: imageQuality },
      html2canvas: {
        scale,
        useCORS: true,
        letterRendering: true,
        backgroundColor: '#ffffff',
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait' as const,
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    await html2pdf().set(opt).from(container).save();
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

const reportTitleFor = (entityType: MarkdownToPDFOptions['entityType']): string => {
  switch (entityType) {
    case 'use_case':
      return i18n.t('reportModal.reportUseCase');
    case 'cadrage':
      return i18n.t('reportModal.reportCadrage');
    case 'domain':
      return i18n.t('reportModal.reportDomain');
    default:
      return i18n.t('reportModal.reportGeneric');
  }
};

/**
 * Generate a PDF from Markdown content
 * Converts markdown to HTML first, then generates PDF
 */
export const generatePDFFromMarkdown = async (options: MarkdownToPDFOptions): Promise<void> => {
  const { markdownContent, filename, entityName, entityType } = options;

  // Convert markdown to HTML (basic conversion)
  const htmlContent = markdownToHTML(markdownContent);

  // Add header if entity info provided
  let finalHTML = htmlContent;
  if (entityName) {
    const generatedAt = new Date().toLocaleDateString(i18n.language?.startsWith('en') ? 'en-US' : 'fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    finalHTML = `
      <div style="margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #ea580c;">
        <h1 style="color: #ea580c; margin: 0 0 8px 0; font-size: 28px;">${reportTitleFor(entityType)}</h1>
        <p style="color: #666; margin: 0; font-size: 16px;">${entityName}</p>
        <p style="color: #999; margin: 8px 0 0 0; font-size: 12px;">${i18n.t('reportModal.generatedOn', { date: generatedAt })}</p>
      </div>
      ${htmlContent}
    `;
  }

  await generatePDFFromHTML({
    htmlContent: finalHTML,
    filename,
  });
};

/**
 * Open content in a new window for printing
 */
export const openPrintWindow = (htmlContent: string, title: string): void => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Could not open print window');
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #1a1a1a;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
          }
          h1 { font-size: 24px; margin-bottom: 16px; color: #111; border-bottom: 2px solid #333; padding-bottom: 8px; }
          h2 { font-size: 20px; margin-top: 24px; margin-bottom: 12px; color: #222; }
          h3 { font-size: 16px; margin-top: 20px; margin-bottom: 8px; color: #333; }
          p { margin-bottom: 12px; }
          ul, ol { margin-bottom: 12px; padding-left: 24px; }
          li { margin-bottom: 4px; }
          strong { font-weight: 600; }
          code { background: #f5f5f5; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 14px; }
          pre { background: #f5f5f5; padding: 16px; border-radius: 8px; overflow-x: auto; }
          table { width: 100%; border-collapse: collapse; margin: 16px 0; }
          th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
          th { background: #f5f5f5; font-weight: 600; }
          blockquote { border-left: 4px solid #ddd; margin: 16px 0; padding-left: 16px; color: #666; }
          hr { border: none; border-top: 1px solid #ddd; margin: 24px 0; }
          @media print {
            body { padding: 0; }
            h1, h2, h3 { page-break-after: avoid; }
            pre, blockquote { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

/**
 * Basic Markdown to HTML converter
 * For complex markdown, consider using a library like marked or showdown
 */
const markdownToHTML = (markdown: string): string => {
  let html = markdown
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Unordered lists
    .replace(/^\s*[-*+]\s+(.*)$/gim, '<li>$1</li>')
    // Ordered lists
    .replace(/^\s*\d+\.\s+(.*)$/gim, '<li>$1</li>')
    // Horizontal rule
    .replace(/^---$/gim, '<hr>')
    // Blockquotes
    .replace(/^>\s+(.*)$/gim, '<blockquote>$1</blockquote>')
    // Paragraphs (simple approach)
    .replace(/\n\n/g, '</p><p>')
    // Line breaks
    .replace(/\n/g, '<br>');

  // Wrap list items
  html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');
  // Clean up nested ul tags
  html = html.replace(/<\/ul>\s*<ul>/g, '');

  // Wrap in paragraph if not already wrapped
  if (!html.startsWith('<')) {
    html = `<p>${html}</p>`;
  }

  return html;
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

let docxLibraryPromise: Promise<DocxRuntime> | null = null;

const loadDocxLibrary = async (): Promise<DocxRuntime> => {
  if ((window as Window & { docx?: DocxRuntime }).docx) {
    return (window as Window & { docx?: DocxRuntime }).docx as DocxRuntime;
  }

  if (!docxLibraryPromise) {
    docxLibraryPromise = new Promise<DocxRuntime>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-docx-runtime="true"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          const runtime = (window as Window & { docx?: DocxRuntime }).docx;
          if (runtime) {
            resolve(runtime);
          } else {
            reject(new Error('DOCX runtime loaded without exposing window.docx'));
          }
        }, { once: true });
        existingScript.addEventListener('error', () => {
          reject(new Error('Unable to load DOCX runtime'));
        }, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/docx@9.6.1/dist/index.umd.cjs';
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.dataset.docxRuntime = 'true';
      script.onload = () => {
        const runtime = (window as Window & { docx?: DocxRuntime }).docx;
        if (runtime) {
          resolve(runtime);
        } else {
          reject(new Error('DOCX runtime loaded without exposing window.docx'));
        }
      };
      script.onerror = () => {
        reject(new Error('Unable to load DOCX runtime'));
      };
      document.head.appendChild(script);
    }).catch((error) => {
      docxLibraryPromise = null;
      throw error;
    });
  }

  return docxLibraryPromise;
};

const stripMarkdownInline = (text: string): string =>
  text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/~~(.*?)~~/g, '$1');

const createDocxParagraphs = (
  markdown: string,
  title: string | undefined,
  docx: DocxRuntime
) => {
  const { Paragraph, TextRun, HeadingLevel } = docx;
  const paragraphs: unknown[] = [];
  const lines = markdown.split(/\r?\n/);
  let inCodeBlock = false;
  let codeBuffer: string[] = [];
  let paragraphBuffer: string[] = [];

  const flushParagraph = () => {
    const text = stripMarkdownInline(paragraphBuffer.join(' ').trim());
    if (text) {
      paragraphs.push(
        new Paragraph({
          children: [new TextRun(text)],
          spacing: { after: 180 },
        })
      );
    }
    paragraphBuffer = [];
  };

  const flushCodeBlock = () => {
    if (!codeBuffer.length) return;
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: codeBuffer.join('\n'),
            font: 'Courier New',
          }),
        ],
        spacing: { after: 180 },
      })
    );
    codeBuffer = [];
  };

  if (title) {
    paragraphs.push(
      new Paragraph({
        text: title,
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 240 },
      })
    );
  }

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      flushParagraph();
      if (inCodeBlock) {
        flushCodeBlock();
      }
      inCodeBlock = !inCodeBlock;
      continue;
    }

    if (inCodeBlock) {
      codeBuffer.push(line);
      continue;
    }

    if (!trimmed) {
      flushParagraph();
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      const level = Math.min(headingMatch[1].length, 6);
      const headingMap = {
        1: HeadingLevel.HEADING_1,
        2: HeadingLevel.HEADING_2,
        3: HeadingLevel.HEADING_3,
        4: HeadingLevel.HEADING_4,
        5: HeadingLevel.HEADING_5,
        6: HeadingLevel.HEADING_6,
      } as const;
      paragraphs.push(
        new Paragraph({
          text: stripMarkdownInline(headingMatch[2]),
          heading: headingMap[level as keyof typeof headingMap],
          spacing: { after: 180 },
        })
      );
      continue;
    }

    const bulletMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    if (bulletMatch) {
      flushParagraph();
      paragraphs.push(
        new Paragraph({
          text: stripMarkdownInline(bulletMatch[1]),
          bullet: { level: 0 },
          spacing: { after: 80 },
        })
      );
      continue;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      flushParagraph();
      paragraphs.push(
        new Paragraph({
          text: stripMarkdownInline(orderedMatch[1]),
          spacing: { after: 80 },
        })
      );
      continue;
    }

    paragraphBuffer.push(trimmed);
  }

  flushParagraph();
  flushCodeBlock();

  return paragraphs;
};

/**
 * Download content as a Markdown file
 */
export const downloadMarkdown = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'text/markdown' });
  downloadBlob(blob, `${filename}.md`);
};

/**
 * Download Markdown content as a DOCX file
 */
export const downloadDocxFromMarkdown = async (options: MarkdownToDocxOptions): Promise<void> => {
  const { markdownContent, filename, title } = options;
  const docx = await loadDocxLibrary();
  const { Document, Packer } = docx;

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: createDocxParagraphs(markdownContent, title, docx),
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `${filename}.docx`);
};

const pdfService = {
  generatePDFFromHTML,
  generatePDFFromMarkdown,
  openPrintWindow,
  downloadMarkdown,
  downloadDocxFromMarkdown,
};

export default pdfService;
